/*
 * Copyright (c) 2016-2025 Martin Donath <martin.donath@squidfunk.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import "focus-visible"

import {
  EMPTY,
  NEVER,
  Observable,
  Subject,
  defer,
  delay,
  filter,
  map,
  merge,
  mergeWith,
  shareReplay,
  switchMap,
  tap
} from "rxjs"

import { configuration, feature } from "./_"
import {
  at,
  getActiveElement,
  getOptionalElement,
  requestJSON,
  setLocation,
  setToggle,
  watchDocument,
  watchKeyboard,
  watchLocation,
  watchLocationTarget,
  watchMedia,
  watchPrint,
  watchScript,
  watchViewport
} from "./browser"
import {
  getComponentElement,
  getComponentElements,
  mountAnnounce,
  mountBackToTop,
  mountConsent,
  mountContent,
  mountDialog,
  mountHeader,
  mountHeaderTitle,
  mountPalette,
  mountProgress,
  mountSearch,
  mountSearchHiglight,
  mountSidebar,
  mountSource,
  mountTableOfContents,
  mountTabs,
  watchHeader,
  watchMain
} from "./components"
import {
  SearchIndex,
  fetchSitemap,
  setupAlternate,
  setupClipboardJS,
  setupInstantNavigation,
  setupVersionSelector
} from "./integrations"
import {
  patchEllipsis,
  patchIndeterminate,
  patchScrollfix,
  patchScrolllock
} from "./patches"
import "./polyfills"

import {
  Hex,
  Base64,
  CBC,
  Pkcs7,
  AES,
  Utf8
} from "crypto-es"

/* ----------------------------------------------------------------------------
 * Functions - @todo refactor
 * ------------------------------------------------------------------------- */


function fromHex(hexString) { // https://stackoverflow.com/a/50868276
  return new Uint8Array(hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
}

function fromBase64(base64String) { // https://stackoverflow.com/a/41106346
    return Uint8Array.from(atob(base64String), c => c.charCodeAt(0));
}

function getPromiseFromEvent(item, event) { // https://stackoverflow.com/a/70789108
  return new Promise((resolve) => {
    const listener = () => {
      item.removeEventListener(event, listener);
      resolve();
    }
    item.addEventListener(event, listener);
  })
}

function getKeysFromSession () {
  let keys = {};
  let value;
  Object.keys(sessionStorage).forEach((id) => {
    value = sessionStorage.getItem(id);
    if (value.length == 64) {
      if (window.crypto && window.crypto.subtle) {
        keys[id] = fromHex(value);
      } else {
        keys[id] = Hex.parse(value);
      }
    }
  });
  return keys;
}

async function getCryptoKey(rawKey) {
  if (window.crypto && window.crypto.subtle) {
    return await crypto.subtle.importKey(
      "raw",
      rawKey,
      "AES-CBC",
      true,
      ["decrypt"]
    );
  } else {
    return rawKey;
  }
}

/* Split cyphertext bundle and try to decrypt it */
async function decrypt_content_from_bundle(key, ciphertext_bundle) {
  // grab the ciphertext bundle and try to decrypt it
  if (ciphertext_bundle) {
    let parts = ciphertext_bundle.split(';');
    if (parts.length == 2) {
      return await decrypt_content(key, parts[0], parts[1]);
    }
  }
  return false;
}

/* Decrypts the content from the ciphertext bundle. */
async function decrypt_content(key, iv_b64, ciphertext_b64) {
    if (window.crypto && window.crypto.subtle) {
      const iv = fromBase64(iv_b64);
      const ciphertext = fromBase64(ciphertext_b64);
      try {
        const decrypted = await crypto.subtle.decrypt(
          {
            name: "AES-CBC",
            iv: iv
          },
          key,
          ciphertext
        );
        const decoder = new TextDecoder();
        return decoder.decode(decrypted);
      }
      catch (err) {
        // encoding failed; wrong key
        return false;
      }
    } else {
      const iv = Base64.parse(iv_b64);
      const encrypted = {ciphertext: Base64.parse(ciphertext_b64)};
      const cfg = {iv: iv, mode: CBC, padding: Pkcs7};
      const plaintext = AES.decrypt(encrypted, key, cfg);
      if(plaintext.sigBytes >= 0) {
        try {
          return plaintext.toString(Utf8)
        } catch (err) {
          // encoding failed; wrong key
          return false;
        }
      } else {
        // negative sigBytes; wrong key
        return false;
      }
    }
}

async function readEncryptedSearchIndex(encrypted_search) {
  let encryption_keys = getKeysFromSession(); //try to get encryption keys from sessionStorage
  let could_decrypt = false;
  let decrypted_docs = [];

  if (typeof(encryptcontent_event) == "undefined") {
    //A normal page. No decrypt-form.tpl got injected
    //console.log('A normal page. No decrypt-form.tpl got injected');
  } else {
    if (encryptcontent_done) {
      //encryptcontent event already dispatched.
      //console.log('encryptcontent event already dispatched.');
      const listener = () => {
        window.removeEventListener('encryptcontent_event', listener);
        window.location.reload();
      }
      window.addEventListener('encryptcontent_event', listener);
    } else {
      //Wait for the encryptcontent event to get dispatched.
      //console.log('Wait for the encryptcontent event to get dispatched.');
      await getPromiseFromEvent(window, 'encryptcontent_event');
      const listener = () => {
        window.removeEventListener('encryptcontent_event', listener);
        window.location.reload();
      }
      window.addEventListener('encryptcontent_event', listener);
    }
  }
  for (let key_id in encrypted_search) {
    let keys = {}; //crypto keys
    if (key_id in encryption_keys) {
      let key;
      if (key_id in keys) {
          key = keys[key_id];
      } else {
          key = await getCryptoKey(encryption_keys[key_id]);
          keys[key_id] = key;
      }
      if (key) { //we got a valid key
        let entries_decrypted = await decrypt_content_from_bundle(key, encrypted_search[key_id]);
        if (entries_decrypted) {
          could_decrypt = true;
          const encrypted_docs = JSON.parse(entries_decrypted);
          for (let i = 0; i < encrypted_docs.length; i++) {
            decrypted_docs.push(encrypted_docs[i]);
          }
        } else {
          keys[key_id] = null; //set key to invalid
        }
      }
    }
  }
  if (could_decrypt) {
    console.log('Could decrypt entries from encrypted_index');
  }
  return decrypted_docs;
}

/**
 * Fetch search index
 *
 * @returns Search index observable
 */
function fetchSearchIndex(): Observable<SearchIndex> {
  if (location.protocol === "file:") {
    return watchScript(
      `${new URL("search/search_index.js", config.base)}`
    )
      .pipe(
        // @ts-ignore - @todo fix typings
        map(() => __index),
        shareReplay(1)
      )
  } else {
    return requestJSON<SearchIndex>(
      new URL("search/search_index.json", config.base)
    ).pipe(
      tap(index => console.log('search_index:',index)),
      switchMap(searchIndex =>
        requestJSON<EncryptedIndex>(
          new URL("search/encrypted_search_index.json", config.base)
        ).pipe(
          switchMap(index => readEncryptedSearchIndex(index)),
          tap(index => console.log('decrypted_index:',index)),
          map(EncryptedIndex => ({
              ...searchIndex,
              docs: [
                ...searchIndex.docs,
                ...EncryptedIndex
              ]
            })
          ),
          tap(index => console.log('updated_search_index:',index)),
        )
      ),
      shareReplay(1)
    )
  }
}

/* ----------------------------------------------------------------------------
 * Application
 * ------------------------------------------------------------------------- */

/* Yay, JavaScript is available */
document.documentElement.classList.remove("no-js")
document.documentElement.classList.add("js")

/* Set up navigation observables and subjects */
const document$ = watchDocument()
const location$ = watchLocation()
const target$   = watchLocationTarget(location$)
const keyboard$ = watchKeyboard()

/* Set up media observables */
const viewport$ = watchViewport()
const tablet$   = watchMedia("(min-width: 60em)")
const screen$   = watchMedia("(min-width: 76.25em)")
const print$    = watchPrint()

/* Retrieve search index, if search is enabled */
const config = configuration()
const index$ = document.forms.namedItem("search")
  ? fetchSearchIndex()
  : NEVER

/* Set up Clipboard.js integration */
const alert$ = new Subject<string>()
setupClipboardJS({ alert$ })

/* Set up language selector */
setupAlternate({ document$ })

/* Set up progress indicator */
const progress$ = new Subject<number>()

/* Set up sitemap for instant navigation and previews */
const sitemap$ = fetchSitemap(config.base)

/* Set up instant navigation, if enabled */
if (feature("navigation.instant"))
  setupInstantNavigation({ sitemap$, location$, viewport$, progress$ })
    .subscribe(document$)

/* Set up version selector */
if (config.version?.provider === "mike")
  setupVersionSelector({ document$ })

/* Always close drawer and search on navigation */
merge(location$, target$)
  .pipe(
    delay(125)
  )
    .subscribe(() => {
      setToggle("drawer", false)
      setToggle("search", false)
    })

/* Set up global keyboard handlers */
keyboard$
  .pipe(
    filter(({ mode }) => mode === "global")
  )
    .subscribe(key => {
      switch (key.type) {

        /* Go to previous page */
        case "p":
        case ",":
          const prev = getOptionalElement<HTMLLinkElement>("link[rel=prev]")
          if (typeof prev !== "undefined")
            setLocation(prev)
          break

        /* Go to next page */
        case "n":
        case ".":
          const next = getOptionalElement<HTMLLinkElement>("link[rel=next]")
          if (typeof next !== "undefined")
            setLocation(next)
          break

        /* Expand navigation, see https://bit.ly/3ZjG5io */
        case "Enter":
          const active = getActiveElement()
          if (active instanceof HTMLLabelElement)
            active.click()
      }
    })

/* Set up patches */
patchEllipsis({ viewport$, document$ })
patchIndeterminate({ document$, tablet$ })
patchScrollfix({ document$ })
patchScrolllock({ viewport$, tablet$ })

/* Set up header and main area observable */
const header$ = watchHeader(getComponentElement("header"), { viewport$ })
const main$ = document$
  .pipe(
    map(() => getComponentElement("main")),
    switchMap(el => watchMain(el, { viewport$, header$ })),
    shareReplay(1)
  )

/* Set up control component observables */
const control$ = merge(

  /* Consent */
  ...getComponentElements("consent")
    .map(el => mountConsent(el, { target$ })),

  /* Dialog */
  ...getComponentElements("dialog")
    .map(el => mountDialog(el, { alert$ })),

  /* Color palette */
  ...getComponentElements("palette")
    .map(el => mountPalette(el)),

  /* Progress bar */
  ...getComponentElements("progress")
    .map(el => mountProgress(el, { progress$ })),

  /* Search */
  ...getComponentElements("search")
    .map(el => mountSearch(el, { index$, keyboard$ })),

  /* Repository information */
  ...getComponentElements("source")
    .map(el => mountSource(el))
)

/* Set up content component observables */
const content$ = defer(() => merge(

  /* Announcement bar */
  ...getComponentElements("announce")
    .map(el => mountAnnounce(el)),

  /* Content */
  ...getComponentElements("content")
    .map(el => mountContent(el, { sitemap$, viewport$, target$, print$ })),

  /* Search highlighting */
  ...getComponentElements("content")
    .map(el => feature("search.highlight")
      ? mountSearchHiglight(el, { index$, location$ })
      : EMPTY
    ),

  /* Header */
  ...getComponentElements("header")
    .map(el => mountHeader(el, { viewport$, header$, main$ })),

  /* Header title */
  ...getComponentElements("header-title")
    .map(el => mountHeaderTitle(el, { viewport$, header$ })),

  /* Sidebar */
  ...getComponentElements("sidebar")
    .map(el => el.getAttribute("data-md-type") === "navigation"
      ? at(screen$, () => mountSidebar(el, { viewport$, header$, main$ }))
      : at(tablet$, () => mountSidebar(el, { viewport$, header$, main$ }))
    ),

  /* Navigation tabs */
  ...getComponentElements("tabs")
    .map(el => mountTabs(el, { viewport$, header$ })),

  /* Table of contents */
  ...getComponentElements("toc")
    .map(el => mountTableOfContents(el, {
      viewport$, header$, main$, target$
    })),

  /* Back-to-top button */
  ...getComponentElements("top")
    .map(el => mountBackToTop(el, { viewport$, header$, main$, target$ }))
))

/* Set up component observables */
const component$ = document$
  .pipe(
    switchMap(() => content$),
    mergeWith(control$),
    shareReplay(1)
  )

/* Subscribe to all components */
component$.subscribe()

/* ----------------------------------------------------------------------------
 * Exports
 * ------------------------------------------------------------------------- */

window.document$  = document$          /* Document observable */
window.location$  = location$          /* Location subject */
window.target$    = target$            /* Location target observable */
window.keyboard$  = keyboard$          /* Keyboard observable */
window.viewport$  = viewport$          /* Viewport observable */
window.tablet$    = tablet$            /* Media tablet observable */
window.screen$    = screen$            /* Media screen observable */
window.print$     = print$             /* Media print observable */
window.alert$     = alert$             /* Alert subject */
window.progress$  = progress$          /* Progress indicator subject */
window.component$ = component$         /* Component observable */
